import React from "react";

import { Skeleton } from "antd";
import SkeletonButton from "antd/es/skeleton/Button";
import { motion, AnimatePresence } from "framer-motion/dist/framer-motion";
import { has } from "lodash";
import Highlighter from "react-highlight-words";

import Loading from "../../../../Common/Loading";
import NoResults from "../../../../Common/NoResults";
import StyledScrollbar from "../../../../Common/UIComponents/StyledScrollbar";

import emptyStateIcon from "../../../../../assets/images/EmptyState_V2.png";
import listItemArrowIcon from "../../../../../assets/images/product/specmanager/arrow-right-s-line.svg";
import addedStatusIcon from "../../../../../assets/images/product/specmanager/checkbox-circle-line.svg";
import pendingIcon from "../../../../../assets/images/product/specmanager/circle-line.svg";
import missingSpecIcon from "../../../../../assets/images/product/specmanager/error-warning-fill.svg";

import "./SpecDisplayTable.css";

/**
 * @param {unknown} value - API field (string or other); display-only, no numeric parsing
 * @returns {string | null} Trimmed text to show, or null if absent, empty, or "0"
 */
function getCountDisplay(value) {
  if (value === undefined || value === null) return null;
  const s = String(value).trim();
  if (s === "" || s === "0") return null;
  return s;
}

function ProductMetaChips({ item }) {
  const totalTests = getCountDisplay(item.total_tests_count);
  const configuredTests = getCountDisplay(item.configured_tests_count);
  const unconfiguredTests = getCountDisplay(item.unconfigured_tests_count);
  if (totalTests == null && configuredTests == null && unconfiguredTests == null) {
    return null;
  }
  return (
    <div className="specTableRowMetaChips">
      {totalTests != null && (
        <span className="specTableRowMetaChip">
          {totalTests === "1" ? "1 Test" : `${totalTests} Tests`}
        </span>
      )}
      {configuredTests != null && (
        <span className="specTableRowMetaChip">
          {`${configuredTests} Configured`}
        </span>
      )}
      {unconfiguredTests != null && (
        <span className="specTableRowMetaChip specTableRowMetaChip--missing">
          <img src={missingSpecIcon} alt="" className="specTableRowMetaChipIcon" />
          {unconfiguredTests === "1" ? "1 Missing Spec" : `${unconfiguredTests} Missing Specs`}
        </span>
      )}
    </div>
  );
}

export default class SpecDisplayTable extends React.Component {
  render() {
    const {
      tableData,
      handleEditSpecClick,
      loadingNewContent,
      specManagerScrollbar,
      loading,
      searchValue,
      fields,
      linkReportJsonFields,
      searchField,
    } = this.props;

    const linkReportFields = fields.slice(0, linkReportJsonFields.length);

    const handleScrollToBottom = (e) => {
      const { handlePageChangeScroll } = this.props;
      const tolerance = 3;
      const bottom = Math.abs(
        Math.ceil(e.contentScrollHeight - e.scrollTop)
            - Math.ceil(e.clientHeight),
      ) <= tolerance;
      if (bottom) {
        handlePageChangeScroll();
      }
    };

    if (!loading && tableData.length === 0) {
      return (
        <StyledScrollbar
          id="noResultsScrollbar"
          className="productReportSpecManagerDisplayTableNoResultsScrollBar"
          disableTrackYWidthCompensation
          disableTracksWidthCompensation
          ref={specManagerScrollbar}
        >
          <NoResults message="No results found" image={emptyStateIcon} />
        </StyledScrollbar>
      );
    }

    return (
      <StyledScrollbar
        disableTrackYWidthCompensation
        disableTracksWidthCompensation
        id="specManagerDisplayTableScrollBar"
        className="productReportSpecManagerDisplayTableScrollBar"
        ref={specManagerScrollbar}
        onScrollStop={(e) => handleScrollToBottom(e)}
      >
        <table className="specTable" id="productReportSpecManagerSpecTable">
          <tbody>
            {loading && !loadingNewContent && (
            <tr key="skeleton">
              <td>
                <div className="specTableRow SpecTableRow__Skeleton">
                  <Skeleton active paragraph={false} title={{ width: 120 }} />
                  <SkeletonButton active width={120} />
                </div>
              </td>
            </tr>
            )}
            <AnimatePresence mode="popLayout" initial={false}>
              {!(loading && !loadingNewContent) && tableData.map((item, i) => (
                <motion.tr
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  // exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.15 }}
                  layout
                  key={i}
                >
                  <td>
                    <div
                      className={`specTableRow${item.status === "added" || item.status === "pending" ? " specTableRow--clickable" : ""}`}
                      role={item.status === "added" || item.status === "pending" ? "button" : undefined}
                      tabIndex={item.status === "added" || item.status === "pending" ? 0 : undefined}
                      onClick={(e) => {
                        if (item.status === "added" || item.status === "pending") {
                          handleEditSpecClick(e, i, item.status);
                        }
                      }}
                      onKeyDown={(e) => {
                        if ((e.key === "Enter" || e.key === " ") && (item.status === "added" || item.status === "pending")) {
                          handleEditSpecClick(e, i, item.status);
                        }
                      }}
                    >
                      <div className="specTableRowLeftCol">
                        <span className="LinkedFieldsSpan">
                          {
                            linkReportFields.map(({ json_field, title_field }, index) => (has(item.identifier, json_field) ? (
                              <React.Fragment key={json_field}>
                                <span className="LinkedFieldTitle">{`${title_field}:`}</span>
                                <Highlighter
                                  highlightClassName="searchHighlight"
                                  searchWords={json_field === searchField
                                    ? [searchValue]
                                    : []}
                                  autoEscape
                                  textToHighlight={item.identifier[json_field]}
                                  key={`${item.identifier[json_field]}_${index}`}
                                  className="LinkedFieldValue"
                                />
                                {index < Object.keys(item.identifier).length - 1 && (
                                <span className="LinkedFieldsDelimiterDot" />
                                )}
                              </React.Fragment>
                            ) : null))
                          }
                        </span>
                        <ProductMetaChips item={item} />
                      </div>
                      <div className="specTableRowRightCol">
                        {item.status === "added" && (
                        <div className="specStatusChip specStatusChip--added" id="productReportSpecManagerSpecEditButton">
                          <img
                            className="specAdded"
                            src={addedStatusIcon}
                            alt="addedIcon"
                            id="productReportSpecManagerSpecAddedIcon"
                          />
                          Spec Added
                        </div>
                        )}
                        {item.status === "pending" && (
                        <div className="specStatusChip specStatusChip--pending" id="productReportSpecManagerSpecAddButton">
                          <img
                            src={pendingIcon}
                            alt="pendingIcon"
                            id="productReportSpecManagerPendingIcon"
                          />
                          Incomplete
                        </div>
                        )}
                        <img src={listItemArrowIcon} alt="open spec item" className="specTableRowChevronIcon" />
                      </div>
                    </div>
                  </td>
                </motion.tr>
              ))}
            </AnimatePresence>
          </tbody>
        </table>
        {loadingNewContent && <Loading />}
      </StyledScrollbar>
    );
  }
}
